import { applyDecorators } from '@nestjs/common';
import { Transform } from 'class-transformer';
import type { ValidationOptions } from 'class-validator';
import { IsBoolean as ClassValidatorIsBoolean } from 'class-validator';

/**
 * Options for IsBoolean decorator with transformation support.
 */
export interface IsBooleanOptions {
	/**
	 * If true, boolean-like values will be transformed to actual booleans.
	 * Accepted values:
	 * - true, 'true', 1, '1' -> true
	 * - false, 'false', 0, '0' -> false
	 * Any other value will remain unchanged and fail validation.
	 * @default true
	 */
	transform?: boolean;
}

/**
 * Custom IsBoolean decorator that extends class-validator's IsBoolean with transformation support.
 *
 * By default, transforms boolean-like values to actual booleans before validation.
 * Set `transform: false` in options to disable transformation.
 *
 * Accepted values when transform is enabled:
 * - true, 'true', 1, '1' -> true
 * - false, 'false', 0, '0' -> false
 *
 * @example
 * class MyDto {
 *   @IsOptional()
 *   @IsBoolean()
 *   isActive?: boolean; // Accepts true, 'true', 1, '1', false, 'false', 0, '0'
 *
 *   @IsOptional()
 *   @IsBoolean({ transform: false })
 *   strictBoolean?: boolean; // Only accepts actual booleans
 * }
 */
export function IsBoolean(
	options: IsBooleanOptions = {},
	validationOptions?: ValidationOptions,
): PropertyDecorator {
	const { transform = true } = options;

	const decorators: Array<PropertyDecorator> = [];

	if (transform) {
		decorators.push(
			Transform(({ value }: { value: unknown }) => {
				if (typeof value === 'string') {
					value = value.toLowerCase();
				}

				if (
					value === true ||
					value === 'true' ||
					value === 1 ||
					value === '1'
				) {
					return true;
				}

				if (
					value === false ||
					value === 'false' ||
					value === 0 ||
					value === '0'
				) {
					return false;
				}

				return value;
			}),
		);
	}

	decorators.push(ClassValidatorIsBoolean(validationOptions));

	return applyDecorators(...decorators);
}
